import React, {useCallback} from "react";
import {Box, Grid, Switch} from "@mui/material";
import {change, Field, reduxForm} from "redux-form";
import BasicFormTextField from "../../../common/BasicFormTextField";
import {isvalidDateRange, required} from "_helpers";
import CheckboxLabel from "../../../common/CheckboxLabel";
import ActionButton from "../../../common/ActionButton";
import AnchorButton from "../../../common/AnchorButton";
import {connect, useDispatch, useSelector} from "react-redux";
import BasicTimePicker from "../../../common/BasicTimePicker";
import _ from "lodash";
import RatesSelect from "../Utilities/RatesSelect";
import {daysOfWeek, editSchedulePayloadBuilder} from "../Utilities/scheduleUtils";
import moment from "moment";
import {scheduleActions} from "../../../../../_actions";

function EditScheduleForm({ handleSubmit, schedule, payoutRates, pricingRates, scheduleId , orgId, organization }){
    const dispatch = useDispatch();
    const timezone = organization?.timezone || 'No timezone set';
    const startTime = moment.utc(schedule.startTime)
    const endTime = moment.utc(schedule.endTime)

    const schedules = useSelector(state => state.schedules);
    const loadingUpdateSchedule = schedules.loadingUpdateSchedule;

    const onChange = (event) => {
        const name = _.get(event, 'target.name');
        const value = _.get(event, 'target.checked');
        dispatch(change('EditScheduleForm', name, value))
    }

    const handleSelectChange = (event) => {
        const name = _.get(event, 'target.name');
        const value = _.get(event, 'target.value');
        dispatch(change('EditScheduleForm', name, value))
    }

    const onSubmit = useCallback((data) => {
        const payload = editSchedulePayloadBuilder(data)
        dispatch(scheduleActions.updateScheduleV2(payload, scheduleId, orgId))
    },[dispatch, scheduleId, orgId])

    const renderCheckbox = ({ input, label }) => (
        <CheckboxLabel
            name={input.name}
            checked={Boolean(input.value)}
            value={input.name}
            label={label}
            onChange={(event, isChecked) => input.onChange(isChecked)}
        />
    );

    return(
        <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container mt={{ xs: '14px', sm: '20px'}}>
                <Grid item sx={styles.formContainer} xs={12} md={6} lg={6}>
                    <Box mb={{xs: "10px", sm: "10px", md: "15px", lg: "15px"}}>
                        <Box sx={styles.inputDesc}>Schedule name</Box>
                        <Field
                            type="text"
                            name="name"
                            component={BasicFormTextField}
                            validate={required}
                        />
                    </Box>

                    <Box mb={{xs: "10px", sm: "10px", md: "15px", lg: "15px"}}>
                        <Box sx={styles.inputDesc}>Days</Box>
                        <Grid container>
                            {daysOfWeek.map(day => (
                                <Grid item xs={3} lg={3} key={day}>
                                    <Field
                                        name={day}
                                        component={renderCheckbox}
                                        type="checkbox"
                                        label={day}
                                        onChange={onChange}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>

                    <Box mb={{xs: "10px", sm: "10px", md: "15px", lg: "15px"}}>
                        <Grid container spacing={2}>
                            <Grid item xs={6} md={4}>
                                <Box sx={styles.inputDesc}>Start time</Box>
                                <Field
                                    name="startTime"
                                    component={BasicTimePicker}
                                    type="text"
                                    showSecond={false}
                                    applyValidation={true}
                                    defaultValue={startTime}
                                />
                            </Grid>
                            <Grid item xs={6} md={4}>
                                <Box sx={styles.inputDesc}>End time</Box>
                                <Field
                                    name="endTime"
                                    component={BasicTimePicker}
                                    type="text"
                                    showSecond={false}
                                    applyValidation={true}
                                    defaultValue={endTime}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Box sx={styles.inputDesc}>Timezone</Box>
                                <Box sx={{color: "#0C2728", fontSize: 14, fontFamily: 'larsseitRegular'}} >{timezone}</Box>
                            </Grid>
                        </Grid>
                    </Box>

                    <Box mb={{xs: "10px", sm: "10px", md: "15px", lg: "15px"}}>
                        <Box sx={styles.inputDesc}>Payout rates</Box>
                        <Field
                            name="payoutRate"
                            component={RatesSelect}
                            options={payoutRates}
                            defaultValue={schedule?.payoutRate?.id}
                            handleChange={handleSelectChange}
                        />
                    </Box>

                    <Box mb={{xs: "10px", sm: "10px", md: "15px", lg: "15px"}}>
                        <Box sx={styles.inputDesc}>Pricing rates</Box>
                        <Field
                            name="pricingRate"
                            component={RatesSelect}
                            options={pricingRates}
                            defaultValue={schedule?.pricingRate?.id}
                            handleChange={handleSelectChange}
                        />
                    </Box>

                    <Box>
                        <Box sx={styles.inputDesc}>
                           Is enabled
                        </Box>
                        <Box sx={{mt: '4px'}}>
                            <Switch defaultChecked={schedule.isActive} name='isActive' onChange={onChange} sx={styles.switch} />
                        </Box>
                    </Box>

                    <Box sx={styles.footer}>
                        <Box sx={{width: '100px'}}>
                            <ActionButton
                                label={'Save'}
                                disabled={loadingUpdateSchedule}
                            />
                        </Box>
                        <Box sx={{width: '100px'}}>
                            <AnchorButton
                                label={'Cancel'}
                                link={`/organizations/${orgId}/settings#expert`}
                                buttonBordered={true}
                            />
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </form>
    )
}

const styles = {
    formContainer: {
        backgroundColor: '#FFF',
        border: '1px solid #CED4D4',
        borderRadius: '5px',
        padding: '20px'
    },
    inputDesc: {
        fontFamily: 'LarsseitMedium',
        fontSize: '14px',
        fontWeight: 500,
        color: '#495D5E',
        mb: '7px'
    },
    footer: {
        display: 'flex',
        gap: '18px',
        paddingTop: '60px'
    },
    switch: {
        "& .MuiSwitch-switchBase.Mui-checked": {
            color: '#003300'
        },
        "& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track": {
            backgroundColor: '#003300'
        },
        "&:hover .MuiSwitch-switchBase": {
            color: '#003300'
        },
        "& .MuiSwitch-switchBase.Mui-disabled.Mui-checked": {
            color: '#003300',
        },
    },
}

const formConfig = {
    form: 'EditScheduleForm',
    enableReinitialize: true,
};

EditScheduleForm = reduxForm(formConfig)(EditScheduleForm)
EditScheduleForm = connect(state => {
    const schedule = _.get(state, 'schedules.current', {});
    const days = {}

    schedule?.days?.forEach(day => {
        days[day] = true
    });

    return {
        initialValues: {
            name: _.get(schedule, 'name'),
            isActive: _.get(schedule, 'isActive'),
            payoutRate: _.get(schedule, 'payoutRate.id'),
            pricingRate:  _.get(schedule, 'pricingRate.id'),
            startTime: _.get(schedule, 'startTime'),
            endTime: _.get(schedule, 'endTime'),
            ...days
        }
    };
})(EditScheduleForm);

export default EditScheduleForm